import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ActivityFeed } from '../../components/portal/ActivityFeed';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { CopyClientLink } from '../../components/portal/CopyClientLink';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';
interface ProjectsProps {
  /** The New Project form is opened from the header. */
  creating: boolean;
  setCreating: (open: boolean) => void;
}
export function Projects({
  creating,
  setCreating
}: ProjectsProps) {
  const {
    activeProjects,
    archivedProjects,
    templates,
    openProject,
    createProject,
    findExistingProject,
    reopenProject,
    notify
  } = usePortal();
  const [client, setClient] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  /** An existing project with the same client and name, awaiting a decision. */
  const [duplicate, setDuplicate] = useState<{
    id: string;
    client: string;
    name: string;
  } | null>(null);
  function create() {
    createProject({
      client: client.trim(),
      name: name.trim(),
      type: type.trim(),
      templateId
    });
    notify(`${client.trim()} project created`);
    setClient('');
    setName('');
    setType('');
    setTemplateId('');
    setDuplicate(null);
    setCreating(false);
  }

  /* Editing either field after the warning shows means it was about a pairing
     that no longer exists — clear it so the next submit checks fresh, rather
     than skipping the check (and possibly opening a stale match) because
     `duplicate` was still set from before the edit. */
  function editClient(value: string) {
    setClient(value);
    if (duplicate) setDuplicate(null);
  }
  function editName(value: string) {
    setName(value);
    if (duplicate) setDuplicate(null);
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client.trim() || !name.trim()) return;

    /* A warning, not a block — Portal may legitimately repeat a name. Always
       re-checked here rather than trusted from state, so a second submit
       after editing the fields evaluates the CURRENT client/name, not
       whatever pairing an earlier warning was about. */
    const existing = findExistingProject(client.trim(), name.trim());
    if (existing) {
      setDuplicate({
        id: existing.id,
        client: existing.client,
        name: existing.name
      });
      return;
    }
    create();
  }

  /* The archive: the same list, kept off the working dashboard. */
  if (showArchive) {
    return <div data-id="element-802" data-mp="uliunpm39gde">
        <button type="button" onClick={() => setShowArchive(false)} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-803" data-mp="um2bf3005ypu">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-804" data-mp="2k5sicvf289dm" />
          Client Projects
        </button>

        <h1 className="mt-10 border-b border-ruleDark pb-8 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-805" data-mp="25et9rke85ku1">
          Archived Projects
        </h1>

        <ul className="m-0 list-none p-0" data-id="element-806" data-mp="20h1ld0csggul">
          {archivedProjects.map(row => <li key={row.id} className="grid grid-cols-1 items-baseline gap-x-8 gap-y-4 border-b border-ruleDark py-8 sm:grid-cols-12" data-id="element-807" data-mp="1wck2d3e967kv">
              <span className="font-display text-2xl leading-none sm:col-span-5" data-id="element-808" data-mp="1mgz3he4hdnfc">{row.client}</span>
              <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-809" data-mp="3ls0acfgfxn96">{row.name}</span>
              <span className="flex flex-wrap items-center gap-4 sm:col-span-3 sm:justify-end" data-id="element-810" data-mp="3gv04a4jlcki2">
                <button type="button" onClick={() => openProject(row.id)} className={BUTTON_QUIET} data-id="element-811" data-mp="2ehlpbao6422g">
                  Open Project
                </button>
                <button type="button" onClick={() => {
              reopenProject(row.id);
              notify(`${row.client} restored to Active Projects`);
            }} className={BUTTON_QUIET} data-id="element-812" data-mp="24i4lyzwe209n">
                  Restore Project
                </button>
              </span>
            </li>)}
          {archivedProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-813" data-mp="1qqq26qunpv1j">
              Nothing archived.
            </li>}
        </ul>

        <p className="label-sm mt-8 max-w-xl leading-relaxed text-paper/30" data-id="element-814" data-mp="1bvs9fwvlj3jx">
          Nothing is deleted — deliverables, comments, approvals, version history, project
          information and the client link are all retained.
        </p>
      </div>;
  }
  return <div data-id="element-815" data-mp="t6q5d9yc2yic">
      {creating && <form onSubmit={submit} className="mb-12 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-816" data-mp="t7hnpj2x6zqo">
          <h2 className="font-display text-2xl leading-none" data-id="element-817" data-mp="1733b5lti9bgz">New Project</h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-818" data-mp="124iajlaccps0">
            <label className="block" data-id="element-819" data-mp="1g52vamztgvo5">
              <span className="label block text-paper/40" data-id="element-820" data-mp="1g41jggaz855d">Client</span>
              <input value={client} onChange={event => editClient(event.target.value)} placeholder="e.g. Example Group" autoFocus className={FIELD} data-id="element-821" data-mp="1c37wbp5cyudj" />
            </label>
            <label className="block" data-id="element-822" data-mp="1ztlpjizoiz9p">
              <span className="label block text-paper/40" data-id="element-823" data-mp="1zun1dpoirpsh">Project Name</span>
              <input value={name} onChange={event => editName(event.target.value)} placeholder="e.g. Leadership Series" className={FIELD} data-id="element-824" data-mp="1uvackze388n9" />
            </label>
            <label className="block" data-id="element-825" data-mp="26xl8us349asa">
              <span className="label block text-paper/40" data-id="element-826" data-mp="2bw4qwhela2fd">Project Type</span>
              <input value={type} onChange={event => setType(event.target.value)} placeholder="e.g. Video Series" className={FIELD} data-id="element-827" data-mp="2bu1unfmufd27" />
            </label>
            <label className="block" data-id="element-828" data-mp="2pn060bfolfre">
              <span className="label block text-paper/40" data-id="element-829" data-mp="2plyhp59nmwrq">Start From (optional)</span>
              <select value={templateId} onChange={event => setTemplateId(event.target.value)} className={SELECT} data-id="element-830" data-mp="2unnscjsyt8fa">
                <option value="" className={OPTION} data-id="element-831" data-mp="109ek4h0i3y8r">
                  Blank project
                </option>
                {templates.map(template => <option key={template.id} value={template.id} className={OPTION} data-id="element-832" data-mp="159j2kckyz31m">
                    {template.name}
                  </option>)}
              </select>
            </label>
          </div>
          {duplicate ? <div className="mt-10 max-w-xl border border-paper/30 px-6 py-6" data-id="element-833" data-mp="nxkgjl56fkfn">
              <p className="font-display text-xl leading-snug" data-id="element-834" data-mp="izixh07r3l46">
                A project with this client and project name already exists.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-835" data-mp="1gm9krt1j45vm">
                <button type="button" onClick={() => {
            const id = duplicate.id;
            setDuplicate(null);
            setCreating(false);
            openProject(id);
          }} className={BUTTON_SOLID} data-id="element-836" data-mp="1bp9h1os5jf7o">
                  Open Existing Project
                </button>
                <button type="button" onClick={create} className={BUTTON_QUIET} data-id="element-837" data-mp="3meyb0bfc8cu2">
                  Create Anyway
                </button>
                <button type="button" onClick={() => setDuplicate(null)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-838" data-mp="g0tzjkrhp2dz">
                  Cancel
                </button>
              </div>
            </div> : <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-839" data-mp="yoqkkjpcento">
              <button type="submit" className={BUTTON_SOLID} data-id="element-840" data-mp="yrl5sdg6kco5">
                Create Project
              </button>
              <button type="button" onClick={() => setCreating(false)} className={BUTTON_QUIET} data-id="element-841" data-mp="px6pc7jwyw3g">
                Cancel
              </button>
            </div>}
          <p className="label-sm mt-8 max-w-lg leading-relaxed text-paper/30" data-id="element-842" data-mp="twfhj7zb2lww">
            Templates are starting structures only — every deliverable can be added, renamed,
            reordered or removed afterwards.
          </p>
        </form>}

      <NeedsAttention data-id="element-843" data-mp="3746qrrky7grn" />

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-844" data-mp="373nkx98koitz">
        <section aria-labelledby="active-projects" className="lg:col-span-8" data-id="element-845" data-mp="3253titb7meu8">
          <h2 id="active-projects" className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-846" data-mp="3gvl00r2pbuxz">
            Active Projects
          </h2>
          <ul className="m-0 list-none p-0" data-id="element-847" data-mp="2o6e0ppvfb1bo">
            {activeProjects.map(row => {
            const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
            const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
            return <li key={row.id} className="border-b border-ruleDark" data-id="element-848" data-mp="8f8a5jwsd3ei">
                  <div className="grid grid-cols-1 items-baseline gap-x-8 gap-y-3 sm:grid-cols-12" data-id="element-849" data-mp="8eyizt008q78">
                    {/* The project name stays the dominant item in the row. */}
                    <button type="button" onClick={() => openProject(row.id)} className="group grid grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:col-span-10 sm:grid-cols-10" data-id="element-850" data-mp="1o2o56fllfeqv">
                      <span className="font-display text-3xl leading-none sm:col-span-6 sm:text-4xl" data-id="element-851" data-mp="19cp3w3yqrn76">
                        {row.client}
                      </span>
                      <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-852" data-mp="10br2ltfottsa">
                        {row.name}
                      </span>
                      <span className="label flex items-center gap-4 text-paper sm:col-span-10" data-id="element-853" data-mp="ve8o7niwimcw">
                        {changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row)}
                        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-854" data-mp="2pr8bm2tiwkuq" />
                      </span>
                    </button>
                    <span className="pb-8 sm:col-span-2 sm:justify-self-end sm:pt-8" data-id="element-855" data-mp="372djv9bxwdp2">
                      <CopyClientLink slug={row.slug} projectId={row.id} data-id="element-856" data-mp="3c16y53z1w8qa" />
                    </span>
                  </div>
                </li>;
          })}
            {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-857" data-mp="2o9jdh3sdttwg">
                Nothing in production.
              </li>}
          </ul>

          {archivedProjects.length > 0 && <button type="button" onClick={() => setShowArchive(true)} className="label-sm mt-10 text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-858" data-mp="3k3rqn80svdo">
              Archived Projects · {archivedProjects.length}
            </button>}
        </section>

        <div className="lg:col-span-4 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-859" data-mp="1zotg1z4x36oq">
          <ActivityFeed data-id="element-860" data-mp="2dk452vwhcxl6" />
        </div>
      </div>
    </div>;
}